import { useMemo } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import toastr from "toastr";
import { withTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useUpdateWorkerUser } from "../../../api/users";
import { useGetAllCertsNotPaginated } from "../../../api/certs";
import { PROFILE_AVAILABILITY_STATUS } from "../../../constants/workerOnboarding";
import { buildWorkerProfileFormData } from "../../update-profile/worker-profile/buildWorkerProfileFormData";
import { buildProfileAvailabilityValidationSchema } from "../../update-profile/worker-profile/workerProfileValidation";

function WorkerProfileAvailabilityCard({
  formValues,
  onSaved,
  t,
}) {
  const updateUser = useUpdateWorkerUser();
  const { data: certsDataRes } = useGetAllCertsNotPaginated();
  const certsData = useMemo(() => {
    const raw = certsDataRes?.data?.data || certsDataRes?.data || [];
    return Array.isArray(raw) ? raw : [];
  }, [certsDataRes]);

  const tt = (key, fallback) => t(key, { defaultValue: fallback ?? key });

  const initialAvailability = formValues?.profileAvailability ?? {
    status: PROFILE_AVAILABILITY_STATUS.AVAILABLE,
    unavailableTill: "",
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialAvailability,
    validationSchema: buildProfileAvailabilityValidationSchema(t),
    onSubmit: async (availabilityValues, { setSubmitting }) => {
      if (!formValues) {
        setSubmitting(false);
        return;
      }
      try {
        const merged = {
          ...formValues,
          profileAvailability: availabilityValues,
        };
        const fd = buildWorkerProfileFormData(merged, certsData);
        await updateUser.mutateAsync(fd);
        toastr.success(
          t(
            "workerDashboardAvailabilitySaved",
            "Availability updated successfully."
          )
        );
        onSaved?.();
      } catch (e) {
        toastr.error(
          e?.response?.data?.message ||
            t("tryAgain") ||
            "Update failed. Please try again."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue, setFieldTouched } =
    formik;

  const invalid = (field) =>
    !!(touched[field] && errors[field]);

  const handleStatusChange = (status) => {
    setFieldValue("status", status);
    if (status === PROFILE_AVAILABILITY_STATUS.AVAILABLE) {
      setFieldValue("unavailableTill", "");
    }
    setFieldTouched("status", true, false);
  };

  return (
    <Card className="worker-dashboard-section border-0 shadow-sm mb-4">
      <CardBody className="p-4">
        <h5 className="worker-dashboard-section__title mb-4">
          {tt("profileAvailabilityTitle", "Profile availability")}
        </h5>
        <form onSubmit={handleSubmit}>
          <Row className="gy-3">
            <Col md={12}>
              <Label className="fw-bolder">
                {tt("profileAvailabilityStatus", "Status")}
              </Label>
              <div className="d-flex flex-wrap gap-3">
                <div className="form-check">
                  <Input
                    id="dashboardProfileAvailabilityAvailable"
                    className="form-check-input"
                    type="radio"
                    name="status"
                    checked={
                      values.status === PROFILE_AVAILABILITY_STATUS.AVAILABLE
                    }
                    onChange={() =>
                      handleStatusChange(PROFILE_AVAILABILITY_STATUS.AVAILABLE)
                    }
                    onBlur={handleBlur}
                    invalid={invalid("status")}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="dashboardProfileAvailabilityAvailable"
                  >
                    {tt("profileAvailabilityAvailable", "Available")}
                  </Label>
                </div>
                <div className="form-check">
                  <Input
                    id="dashboardProfileAvailabilityUnavailable"
                    className="form-check-input"
                    type="radio"
                    name="status"
                    checked={
                      values.status === PROFILE_AVAILABILITY_STATUS.UNAVAILABLE
                    }
                    onChange={() =>
                      handleStatusChange(
                        PROFILE_AVAILABILITY_STATUS.UNAVAILABLE
                      )
                    }
                    onBlur={handleBlur}
                    invalid={invalid("status")}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="dashboardProfileAvailabilityUnavailable"
                  >
                    {tt("profileAvailabilityUnavailable", "Unavailable")}
                  </Label>
                </div>
              </div>
              {invalid("status") && (
                <div className="invalid-feedback d-block">{errors.status}</div>
              )}
            </Col>

            {values.status === PROFILE_AVAILABILITY_STATUS.UNAVAILABLE && (
              <Col md={6} lg={4}>
                <Label
                  className="fw-bolder"
                  htmlFor="dashboardProfileAvailabilityUnavailableTill"
                >
                  {tt("profileAvailabilityUnavailableTill", "Unavailable until")}{" "}
                  <span className="text-muted fw-normal">
                    ({tt("optional", "Optional")})
                  </span>
                </Label>
                <div className="d-flex gap-2 align-items-center">
                  <Input
                    type="date"
                    id="dashboardProfileAvailabilityUnavailableTill"
                    name="unavailableTill"
                    value={values.unavailableTill}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={invalid("unavailableTill")}
                  />
                  {values.unavailableTill && (
                    <Button
                      type="button"
                      color="secondary"
                      outline
                      size="sm"
                      onClick={() => {
                        setFieldValue("unavailableTill", "");
                        setFieldTouched("unavailableTill", true, false);
                      }}
                    >
                      {tt("clear", "Clear")}
                    </Button>
                  )}
                </div>
                {invalid("unavailableTill") && (
                  <div className="invalid-feedback d-block">
                    {errors.unavailableTill}
                  </div>
                )}
              </Col>
            )}

            <Col xs={12}>
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    {tt("saving", "Saving...")}
                  </>
                ) : (
                  t("workerDashboardSaveAvailability", "Save availability")
                )}
              </Button>
            </Col>
          </Row>
        </form>
      </CardBody>
    </Card>
  );
}

WorkerProfileAvailabilityCard.propTypes = {
  formValues: PropTypes.object,
  onSaved: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(WorkerProfileAvailabilityCard);
